import React from "react";
import { Descriptions, Tag, Collapse, Space, Typography } from "antd";
import { decodeStr } from "../../../utils";

const { Text } = Typography;

const ColorSwatch = ({ color, label }) => {
  if (!color) return <Text type="secondary">-</Text>;
  return (
    <Space>
      <div
        style={{
          width: 20,
          height: 20,
          backgroundColor: color,
          borderRadius: 4,
          border: "1px solid #d9d9d9",
          display: "inline-block",
        }}
      />
      <Text copyable={{ text: color }}>{color}</Text>
    </Space>
  );
};

const ImageThumbnail = ({ file, alt }) => {
  if (!file?.url) return <Text type="secondary">-</Text>;
  return (
    <a href={file.url} target="_blank" rel="noopener noreferrer">
      <img
        src={file.url}
        alt={alt}
        style={{
          maxHeight: 40,
          maxWidth: 80,
          objectFit: "contain",
          borderRadius: 4,
          border: "1px solid #f0f0f0",
        }}
      />
    </a>
  );
};

const CompanyHeader = ({ company }) => {
  if (!company) return null;

  return (
    <div className="company-header">
      {company.logo?.url && (
        <div style={{ marginBottom: 12 }}>
          <img
            src={company.logo.url}
            alt={company.nome || "Logo"}
            style={{
              height: 48,
              width: "auto",
              objectFit: "contain",
              borderRadius: 6,
              border: "1px solid #f0f0f0",
            }}
          />
        </div>
      )}
      {/* Row 1: Anagrafica */}
      <Descriptions size="small" bordered column={{ xs: 1, sm: 2, md: 3 }}>
        <Descriptions.Item label="Nome">
          {decodeStr(company.nome || "")}
        </Descriptions.Item>
        <Descriptions.Item label="Ragione Sociale">
          {company.ragione_sociale || <Text type="secondary">-</Text>}
        </Descriptions.Item>
        <Descriptions.Item label="P.IVA">
          {company.partita_iva ? (
            <Text copyable>{company.partita_iva}</Text>
          ) : (
            <Text type="secondary">-</Text>
          )}
        </Descriptions.Item>
        <Descriptions.Item label="Codice Fiscale">
          {company.codice_fiscale ? (
            <Text copyable>{company.codice_fiscale}</Text>
          ) : (
            <Text type="secondary">-</Text>
          )}
        </Descriptions.Item>
        <Descriptions.Item label="PEC">
          {company.pec || <Text type="secondary">-</Text>}
        </Descriptions.Item>
        <Descriptions.Item label="Sito Web">
          {company.sito ? (
            <a href={company.sito} target="_blank" rel="noopener noreferrer">
              {company.sito}
            </a>
          ) : (
            <Text type="secondary">-</Text>
          )}
        </Descriptions.Item>
      </Descriptions>

      {/* Row 2: Fatturazione */}
      <Descriptions
        size="small"
        bordered
        column={{ xs: 1, sm: 2, md: 3 }}
        title="Fatturazione"
        style={{ marginTop: 16 }}
      >
        <Descriptions.Item label="Indirizzo">
          {[
            company.indirizzo_fatturazione,
            company.cap_fatturazione,
            company.citta_fatturazione,
            company.provincia_fatturazione,
            company.nazione,
          ]
            .filter(Boolean)
            .join(", ") || <Text type="secondary">-</Text>}
        </Descriptions.Item>
        <Descriptions.Item label="Email Fatturazione">
          {company.email_fatturazione || <Text type="secondary">-</Text>}
        </Descriptions.Item>
        <Descriptions.Item label="Codice Destinatario">
          {company.codice_destinatario ? (
            <Text copyable>{company.codice_destinatario}</Text>
          ) : (
            <Text type="secondary">-</Text>
          )}
        </Descriptions.Item>
        <Descriptions.Item label="Regime Fiscale">
          {company.regime_fiscale || <Text type="secondary">-</Text>}
        </Descriptions.Item>
        <Descriptions.Item label="Fatt. Elettronica">
          {company.fatturazione_elettronica != null ? (
            <Tag color={company.fatturazione_elettronica ? "green" : "default"}>
              {company.fatturazione_elettronica ? "Si" : "No"}
            </Tag>
          ) : (
            <Text type="secondary">-</Text>
          )}
        </Descriptions.Item>
      </Descriptions>

      {/* Row 3: Dati Bancari */}
      <Descriptions
        size="small"
        bordered
        column={{ xs: 1, sm: 2, md: 3 }}
        title="Dati Bancari"
        style={{ marginTop: 16 }}
      >
        <Descriptions.Item label="IBAN">
          {company.iban ? (
            <Text copyable>{company.iban}</Text>
          ) : (
            <Text type="secondary">-</Text>
          )}
        </Descriptions.Item>
        <Descriptions.Item label="Intestatario">
          {company.intestatario_banca || <Text type="secondary">-</Text>}
        </Descriptions.Item>
        <Descriptions.Item label="Banca">
          {company.nome_banca || <Text type="secondary">-</Text>}
        </Descriptions.Item>
      </Descriptions>

      {/* Branding (collapsed) */}
      <Collapse ghost style={{ marginTop: 16 }}>
        <Collapse.Panel header="Branding" key="branding">
          <Descriptions size="small" bordered column={{ xs: 1, sm: 2, md: 4 }}>
            <Descriptions.Item label="Colore Principale">
              <ColorSwatch color={company.colore_principale} />
            </Descriptions.Item>
            <Descriptions.Item label="Colore Secondario">
              <ColorSwatch color={company.colore_secondario} />
            </Descriptions.Item>
            <Descriptions.Item label="Colore Testo">
              <ColorSwatch color={company.colore_testo} />
            </Descriptions.Item>
            <Descriptions.Item label="Colore Testo Intestazione">
              <ColorSwatch color={company.colore_testo_intestazione} />
            </Descriptions.Item>
          </Descriptions>
          <Descriptions
            size="small"
            bordered
            column={{ xs: 1, sm: 2, md: 4 }}
            style={{ marginTop: 12 }}
          >
            <Descriptions.Item label="Logo Completo">
              <ImageThumbnail file={company.logo_completo} alt="Logo completo" />
            </Descriptions.Item>
            <Descriptions.Item label="Logo Sito">
              <ImageThumbnail file={company.logo_sito} alt="Logo sito" />
            </Descriptions.Item>
            <Descriptions.Item label="Timbro">
              <ImageThumbnail file={company.timbro} alt="Timbro" />
            </Descriptions.Item>
            <Descriptions.Item label="Cover Image">
              <ImageThumbnail file={company.cover_image} alt="Cover" />
            </Descriptions.Item>
          </Descriptions>
        </Collapse.Panel>
      </Collapse>
    </div>
  );
};

export default CompanyHeader;
